import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

/**
 * The right drawer is the universal detail surface (Design Principles: layout) — never a blocking modal.
 * Focus moves into it on open and returns on close; Escape closes (WCAG 2.1 AA).
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
}) {
  const intl = useIntl();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <Dialog.Portal>
        <Dialog.Content
          onInteractOutside={(event) => event.preventDefault()}
          className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-border bg-surface shadow-xl motion-safe:animate-[drawer-in_160ms_ease-out]"
        >
          <div className="flex h-12 shrink-0 items-center justify-between border-b border-border px-4">
            <Dialog.Title className="text-sm font-semibold">{title}</Dialog.Title>
            <Dialog.Close
              className="rounded p-1 text-muted hover:bg-hover hover:text-text focus-visible:outline-2 focus-visible:outline-accent"
              aria-label={intl.formatMessage({ id: 'common.close' })}
            >
              <X aria-hidden className="size-4" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">{title}</Dialog.Description>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
